import {
	TagList,
	TagModel,
	TagSelect
} from "client/Components";
import { TagKind, TagSaveModel } from "client/Api/Tags/tags";
import { useSuggestedTagsDataSource } from "client/Features/Tags/useSuggestedTagsDataSource";
import { IValueInputAttachable } from "client/Forms/attachable";
import { AttachTagSelectOptions } from "client/Forms/attachableProps/tagSelect";
import { AlertManager, useAlerts } from "client/Hooks";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	useMemo,
	useState
} from "react";
import { ensureNotNil } from "client/Misc/utils";

export type IntegrationFlowTagSelectProps = {
	attachable: IValueInputAttachable<readonly TagSaveModel[]>;
	isReadOnly: boolean;
	menuTitle: string;
};

export const IntegrationFlowTagSelect = (props: IntegrationFlowTagSelectProps): ReactElement => {
	const {
		attachable,
		isReadOnly,
		menuTitle
	} = props;

	const [searchTerm, setSearchTerm] = useState<string>("");
	const [shouldLoadTags, setShouldLoadTags] = useState<boolean>(false);

	const alertManager: AlertManager = useAlerts();

	const tagSelectOptions = useMemo(
		(): AttachTagSelectOptions => {
			return {
				onError: (message: string): void => {
					alertManager.showError(message);
				},
				maxTagCount: MAX_TAG_COUNT,
				maxTagLength: MAX_TAG_LENGTH,
				doesSupportDelete: !isReadOnly
			};
		},
		[alertManager, isReadOnly]
	);

	const {
		currentItems: suggestedTags,
		getLoadingState
	} = useSuggestedTagsDataSource({
		filtering: {
			selectedTagIds: attachable
				.value
				.map(x => x.id),
			count: MAX_SUGGESTED_TAG_COUNT,
			kind: TagKind.IntegrationFlow,
			shouldLoadData: shouldLoadTags
		},
		searchTerm: searchTerm
	});

	return isReadOnly
		? (
			<List tags={attachable.value}/>
		)
		: (
			<TagSelect
				{...attachable.attachToTagSelect<number>(tagSelectOptions)}
				onSearch={setSearchTerm}
				searchDebounceInterval={250}
				suggestedTags={suggestedTags}
				placeholder={localize("_AddATag")}
				isDisabled={isReadOnly}
				isBusy={getLoadingState() === "loading"}
				onIsSuggestionsBoxOpenChange={setShouldLoadTags}
				menuTitle={menuTitle}
			/>
		);
};
IntegrationFlowTagSelect.displayName = "IntegrationFlowTagSelect";

export const MAX_TAG_COUNT: number = 20;
export const MAX_TAG_LENGTH: number = 30;
export const MAX_SUGGESTED_TAG_COUNT: number = 20;

type ListProps = {
	tags: readonly TagSaveModel[];
};

const List = (props: ListProps): ReactElement => {
	const tags = useMemo(
		(): readonly TagModel<number>[] => {
			return props
				.tags
				.map(
					(tag) => ({
						value: ensureNotNil(tag.id),
						text: tag.name,
						color: "secondary"
					})
				);
		},
		[props.tags]
	);

	return (
		<TagList
			tags={tags}
			showMoreTagsColor="secondary"
		/>
	);
};
List.displayName = "List";